import { openDB } from 'idb';

const DB_NAME = 'HikingBuddyDB';
const DB_VERSION = 1;

// Open/create database
export async function initDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Create object stores if they don't exist
      
      // Store for unsynced hike entries
      if (!db.objectStoreNames.contains('unsyncedEntries')) {
        const unsyncedStore = db.createObjectStore('unsyncedEntries', {
          keyPath: 'tempId',
          autoIncrement: true
        });
        unsyncedStore.createIndex('hikeId', 'hikeId');
        unsyncedStore.createIndex('date', 'date');
      }

      // Store for cached hikes
      if (!db.objectStoreNames.contains('hikes')) {
        const hikesStore = db.createObjectStore('hikes', {
          keyPath: '_id'
        });
        hikesStore.createIndex('userId', 'userId');
      }

      // Store for cached hike entries
      if (!db.objectStoreNames.contains('entries')) {
        const entriesStore = db.createObjectStore('entries', {
          keyPath: '_id'
        });
        entriesStore.createIndex('hikeId', 'hikeId');
        entriesStore.createIndex('date', 'date');
      }
    }
  });
}

// ==================== UNSYNCED ENTRIES ====================

// Add an unsynced entry (created while offline)
export async function addUnsyncedEntry(entry) {
  const db = await initDB();
  const tempId = await db.add('unsyncedEntries', {
    ...entry,
    createdAt: new Date().toISOString()
  });
  return tempId;
}

// Get all unsynced entries
export async function getUnsyncedEntries() {
  const db = await initDB();
  return db.getAll('unsyncedEntries');
}

// Get unsynced entries for a specific hike
export async function getUnsyncedEntriesByHike(hikeId) {
  const db = await initDB();
  return db.getAllFromIndex('unsyncedEntries', 'hikeId', hikeId);
}

// Delete an unsynced entry (after successful sync)
export async function deleteUnsyncedEntry(tempId) {
  const db = await initDB();
  return db.delete('unsyncedEntries', tempId);
}

// Update an existing unsynced entry in place (editing an offline entry while still offline)
export async function updateUnsyncedEntry(tempId, entry) {
  const db = await initDB();
  return db.put('unsyncedEntries', {
    ...entry,
    tempId,
    createdAt: new Date().toISOString()
  });
}

// Clear all unsynced entries
export async function clearUnsyncedEntries() {
  const db = await initDB();
  return db.clear('unsyncedEntries');
}

// ==================== CACHED HIKES ====================

// Cache a hike
export async function cacheHike(hike) {
  const db = await initDB();
  return db.put('hikes', hike);
}

// Cache multiple hikes
export async function cacheHikes(hikes) {
  const db = await initDB();
  const tx = db.transaction('hikes', 'readwrite');
  await Promise.all([
    ...hikes.map(hike => tx.store.put(hike)),
    tx.done
  ]);
}

// Get all cached hikes
export async function getCachedHikes() {
  const db = await initDB();
  return db.getAll('hikes');
}

// Get a specific cached hike
export async function getCachedHike(hikeId) {
  const db = await initDB();
  return db.get('hikes', hikeId);
}

// Delete a cached hike
export async function deleteCachedHike(hikeId) {
  const db = await initDB();
  return db.delete('hikes', hikeId);
}

// ==================== CACHED ENTRIES ====================

// Cache a hike entry
export async function cacheEntry(entry) {
  const db = await initDB();
  return db.put('entries', entry);
}

// Cache multiple entries
export async function cacheEntries(entries) {
  const db = await initDB();
  const tx = db.transaction('entries', 'readwrite');
  await Promise.all([
    ...entries.map(entry => tx.store.put(entry)),
    tx.done
  ]);
}

// Replace the cached entries for a hike with a fresh copy from the server
// (drops entries that were deleted on the server since the last cache)
export async function replaceCachedEntriesForHike(hikeId, entries) {
  const db = await initDB();
  const tx = db.transaction('entries', 'readwrite');
  const existingKeys = await tx.store.index('hikeId').getAllKeys(hikeId);
  await Promise.all([
    ...existingKeys.map(key => tx.store.delete(key)),
    ...entries.map(entry => tx.store.put(entry)),
    tx.done
  ]);
}

// Get all cached entries for a hike
export async function getCachedEntriesByHike(hikeId) {
  const db = await initDB();
  return db.getAllFromIndex('entries', 'hikeId', hikeId);
}

// Get a specific cached entry
export async function getCachedEntry(entryId) {
  const db = await initDB();
  return db.get('entries', entryId);
}

// Delete a cached entry
export async function deleteCachedEntry(entryId) {
  const db = await initDB();
  return db.delete('entries', entryId);
}

// ==================== UTILITIES ====================

// Clear all cached data (useful for logout)
export async function clearAllCache() {
  const db = await initDB();
  await Promise.all([
    db.clear('hikes'),
    db.clear('entries')
  ]);
}

// Get sync status
export async function getSyncStatus() {
  const unsyncedCount = (await getUnsyncedEntries()).length;
  return {
    hasUnsyncedData: unsyncedCount > 0,
    unsyncedCount
  };
}

const dbExports = { openDB, addUnsyncedEntry, getUnsyncedEntries, deleteUnsyncedEntry, cacheEntry };
export default dbExports;
